import {toast} from 'react-toastify';

import {create} from 'zustand';

import {
  createMapService,
  deleteMapServices,
  fetchAerialMapServices,
  fetchMapServices,
  fetchMapServicesNotConverted,
  updateMapService,
  updateMapServiceOrder
} from '@/api/internal/map';
import {MapService} from '@/models/dto/map/map-service';

interface MapServiceStore {
  services: MapService[];
  aerialServices: MapService[];
  loading: boolean;
  hasFetched: boolean;
  loadServices: () => Promise<void>;
  loadAerialServices: () => Promise<void>;
  setServices: (services: MapService[]) => void;
  setAerialServices: (services: MapService[]) => void;
  loadServicesNotConverted: () => Promise<void>;
  addService: (newService: MapService) => Promise<void>;
  updateService: (updatedService: MapService) => Promise<void>;
  deleteServices: (ids: number[]) => Promise<void>;
  reorderServices: (newOrdr: {id: number; ordr: number}[]) => Promise<void>;
}

export const useMapServiceStore = create<MapServiceStore>()((set, get) => ({
  services: [],
  aerialServices: [],
  loading: false,
  hasFetched: false,

  loadServices: async () => {
    if (get().hasFetched) return;

    set({loading: true});

    try {
      const data = await fetchMapServices();
      set({services: data, hasFetched: true});
    } catch (error) {
      console.error('지도 서비스 목록을 불러오는 중 오류 발생:', error);
      toast.error('지도 서비스 목록을 불러오는 중 오류가 발생했습니다.');
    } finally {
      set({loading: false});
    }
  },

  loadAerialServices: async () => {
    if (get().hasFetched) return;

    set({loading: true});

    try {
      const data = await fetchAerialMapServices();
      set({aerialServices: data, hasFetched: true});
    } catch (error) {
      console.error('지도 서비스 목록을 불러오는 중 오류 발생:', error);
      toast.error('지도 서비스 목록을 불러오는 중 오류가 발생했습니다.');
    } finally {
      set({loading: false});
    }
  },

  setServices: (services: MapService[]) => {
    set({services, hasFetched: true});
  },

  setAerialServices: (aerialServices: MapService[]) => {
    set({aerialServices, hasFetched: true});
  },

  loadServicesNotConverted: async () => {
    if (get().hasFetched) return;

    set({loading: true});

    try {
      const data = await fetchMapServicesNotConverted();
      set({services: data, hasFetched: true});
    } catch (error) {
      console.error('지도 서비스 목록을 불러오는 중 오류 발생:', error);
      toast.error('지도 서비스 목록을 불러오는 중 오류가 발생했습니다.');
    } finally {
      set({loading: false});
    }
  },

  addService: async (newService: MapService) => {
    try {
      await createMapService(newService);
      toast.success('지도 서비스가 성공적으로 추가되었습니다.');
    } catch (error) {
      console.error('지도 서비스 추가 중 오류 발생:', error);
      toast.error('지도 서비스 추가 중 오류가 발생했습니다.');
    }
  },

  updateService: async (updatedService: MapService) => {
    try {
      await updateMapService(updatedService.id, updatedService);
    } catch (error) {
      console.error('지도 서비스 업데이트 중 오류 발생:', error);
    }
  },

  deleteServices: async (ids: number[]) => {
    try {
      await deleteMapServices(ids);
      set((state) => ({
        services: state.services.filter((service) => !ids.includes(service.id))
      }));
      toast.success(`${ids.length}개의 지도 서비스가 삭제되었습니다.`);
    } catch (error) {
      console.error('지도 서비스 삭제 중 오류 발생:', error);
      toast.error('지도 서비스 삭제 중 오류가 발생했습니다.');
    }
  },

  reorderServices: async (newOrder: {id: number; ordr: number}[]) => {
    try {
      await updateMapServiceOrder(newOrder);
      set((state) => {
        const reordered = [...state.services];
        newOrder.forEach(({id, ordr}) => {
          const item = reordered.find((svc) => svc.id === id);
          if (item) item.ordr = ordr;
        });
        return {services: reordered};
      });
      toast.success('지도 서비스 순서가 저장되었습니다.');
    } catch (error) {
      console.error('순서 저장 실패:', error);
      toast.error('순서 저장에 실패했습니다.');
    }
  }
}));
